/**
 * The one allowlist for authored news HTML — Task 10.1, requirements 4.1–4.5 and 12.4.
 *
 * ## Why it lives here
 *
 * News `contenido` is the only field in this repository that is authored as HTML and rendered as
 * HTML, in two places: the dashboard's staff preview and the public news detail route. Two copies of
 * an allowlist is two security policies, and the one that drifts is the one nobody is looking at. This
 * module is the shared source both import, so "the dashboard and the site sanitize identically" is a
 * property of the code rather than of a review checklist.
 *
 * It sits in `@apuestapr/contracts` because that is the package both frontends already depend on at
 * runtime, and because this is a statement about what a content payload may contain — the same kind of
 * statement as the schemas beside it.
 *
 * ## Why it is a plain object rather than a DOMPurify `Config`
 *
 * Typing it as `Config` would put `dompurify` in this package's dependencies to describe an object
 * that has no behaviour and touches no DOM. Each app applies `satisfies Config` where it calls the
 * sanitizer, so the shape is still checked against the real library — at the boundary that actually
 * uses it.
 *
 * ## The vocabulary, and what each exclusion is for
 *
 * Allowed tags are the formatting an editor needs for a news post: paragraphs, breaks, emphasis,
 * lists, a quote, subheadings, and links. `h1` is absent because the page owns its own heading and an
 * authored `h1` would produce two. `img` is absent because images arrive through `imagenUrl`, which is
 * origin-checked by `imageReferenceSchema`; an arbitrary authored `<img src>` would be an unchecked
 * outbound request and a tracking pixel by another name.
 *
 * Allowed attributes are `href` and `title` only. `style` is forbidden explicitly as well as by
 * omission, and `script`, `style`, and `iframe` are named in `FORBID_TAGS` for the same
 * belt-and-braces reason: an allowlist plus a denylist means a future edit that widens the first has
 * to also defeat the second.
 *
 * `ALLOWED_URI_REGEXP` accepts exactly two shapes — an absolute `https://` URL, or a single-slash
 * site-relative path. `(?!\/)` after the slash is what rejects a protocol-relative `//host` URL, which
 * is the classic way an authored link smuggles in a third-party origin. `javascript:`, `data:`, and
 * `http:` are all outside the pattern, and `ALLOW_UNKNOWN_PROTOCOLS: false` keeps anything exotic out
 * as well.
 */
/** Formatting an editor may use in news `contenido`. */
export const newsHtmlAllowedTags = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'ul',
    'ol',
    'li',
    'blockquote',
    'h2',
    'h3',
    'h4',
    'a',
];
/** The only attributes that survive sanitization. */
export const newsHtmlAllowedAttributes = ['href', 'title'];
/** Absolute HTTPS, or one-slash site-relative. Nothing else is a URL as far as this policy cares. */
export const newsHtmlAllowedUriPattern = /^(?:https:\/\/|\/(?!\/))/i;
export const newsHtmlSanitizerPolicy = {
    ALLOWED_TAGS: newsHtmlAllowedTags,
    ALLOWED_ATTR: newsHtmlAllowedAttributes,
    ALLOWED_URI_REGEXP: newsHtmlAllowedUriPattern,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_ATTR: ['style'],
    FORBID_TAGS: ['script', 'style', 'iframe'],
    RETURN_TRUSTED_TYPE: false,
};
/**
 * True when a sanitized `href` leaves this site, which is what decides whether a link needs
 * `target="_blank"` and `rel="noopener noreferrer"`. Site-relative links stay in the application.
 *
 * Shared with the sanitizer so both halves of the link decision read the same rule.
 */
export const isExternalNewsHref = (href) => href.startsWith('https://');
//# sourceMappingURL=news-html.js.map